import { Selectable } from '@zip/business-components';
import { ActivationStepKey } from '../ActivationConfig';
import {
  getStoredActivationData,
  type StoredActivationData,
} from '../activationStorage';
import {
  BusinessDetailsAISuggestionResponse,
  BusinessDetailsResponse,
  MerchantApplicationResponse,
} from 'types/responses/activation';

export type ActivationBusinessAddress = {
  unitNumber?: string;
  streetNumber?: string;
  streetName?: string;
  suburb?: string;
  postcode?: string;
  state?: string;
};

export type ActivationStepDataModel = {
  [ActivationStepKey.LocationDetails]: {
    businessAddress?: ActivationBusinessAddress;
    businessAddressDisplay?: string;
    isPartOfFranchise?: boolean;
    hasMultipleLocations?: boolean;
  };
  [ActivationStepKey.TransactionDetails]: {
    averageTransactionValue?: string;
    costOfLargestProductOrService?: string;
    averageShippingTimeFrame?: string;
    hasShippingPolicy?: boolean;
    hasRefundPolicy?: boolean;
  };
  [ActivationStepKey.ProvidedServices]: {
    providesService?: boolean;
    fullPaymentTakeTime?: string;
  };
};

export type ActivationStepData = Partial<ActivationStepDataModel>;
export type ActivationLocationDetailsProps =
  ActivationStepDataModel[ActivationStepKey.LocationDetails];
type ActivationTransactionDetailsProps =
  ActivationStepDataModel[ActivationStepKey.TransactionDetails];
type ActivationProvidedServicesProps =
  ActivationStepDataModel[ActivationStepKey.ProvidedServices];

export type ActivationBusinessDetailsPatch = {
  businessAddress?: ActivationLocationDetailsProps['businessAddress'];
  isPartOfFranchise?: boolean;
  hasMultipleLocations?: boolean;
  averageTransactionValue?: string;
  costOfLargestProductOrService?: string;
  averageShippingTimeFrame?: string;
  hasShippingPolicy?: boolean;
  hasRefundPolicy?: boolean;
  providesService?: boolean;
  fullPaymentTakeTime?: string;
};

export type ActivationPatchPayload = {
  businessDetails?: ActivationBusinessDetailsPatch;
};

export type ActivationAIBannerState = 'none' | 'partial' | 'full';

export const ACTIVATION_FORM_OPTION_KEYS = {
  averageTransactionValue: 'AverageTransactionValue',
  costOfLargestProductOrService: 'CostOfProductOrService',
  averageShippingTimeFrame: 'AverageShippingTimeframe',
  fullPaymentTakeTime: 'FullPaymentTakenTime',
} as const;

type ActivationFormOptionField = keyof typeof ACTIVATION_FORM_OPTION_KEYS;

export const AI_CONFIDENCE_THRESHOLDS = {
  businessLocation: 0.8,
  franchiseGroup: 0.8,
  multipleLocations: 0.8,
  avgTransactionValue: 0.6,
  costOfLargestProduct: 0.6,
  avgShipTime: 0.7,
  shippingPolicy: 0.7,
  returnPolicy: 0.7,
} as const;

export const meetsConfidence = (
  confidence: string | null | undefined,
  threshold: number
): boolean => {
  const parsed = parseFloat(confidence ?? '');
  return !isNaN(parsed) && parsed > threshold;
};

type ActivationOptionDefinition = {
  value: string;
  label: string;
};

const MONEY_RANGE_OPTIONS: ActivationOptionDefinition[] = [
  { value: 'Range0To100', label: '$0 - $100' },
  { value: 'Range100To500', label: '$100 - $500' },
  { value: 'Range500To1000', label: '$500 - $1,000' },
  { value: 'Range1000To3000', label: '$1,000 - $3,000' },
  { value: 'Range3000Plus', label: '$3,000+' },
];

const ACTIVATION_OPTION_DEFINITIONS: Record<
  ActivationFormOptionField,
  ActivationOptionDefinition[]
> = {
  averageTransactionValue: MONEY_RANGE_OPTIONS,
  costOfLargestProductOrService: MONEY_RANGE_OPTIONS,
  averageShippingTimeFrame: [
    { value: 'Range0To7', label: '0 - 7 days' },
    { value: 'Range7To14', label: '8 - 14 days' },
    { value: 'Range15To30', label: '15 - 29 days' },
    { value: 'Range30Plus', label: '30+ days' },
  ],
  fullPaymentTakeTime: [
    {
      value: 'Over30DaysBeforeService',
      label: 'More than 30 days before the service',
    },
    {
      value: 'From14To30DaysBeforeService',
      label: '14 - 30 days before the service',
    },
    {
      value: 'From1To14DaysBeforeService',
      label: '1 - 14 days before the service',
    },
    {
      value: 'CompletitionOfService',
      label: 'Completion of service',
    },
  ],
};

const VALID_SHIPPING_TIME_FRAMES = new Set(
  ACTIVATION_OPTION_DEFINITIONS.averageShippingTimeFrame.map((o) => o.value)
);

// Maps raw AI shipping time text to the ShippingTimeFrameInDays enum value
// expected by the API. The AI may return labels with en-dashes (e.g. '0–7 days')
// which are not valid enum values.
const AI_SHIPPING_TIME_MAP: Record<string, string> = {
  '0–7 days': 'Range0To7',
  '8–14 days': 'Range7To14',
  '15–29 days': 'Range15To30',
  '30+ days': 'Range30Plus',
};

export const mapAIShippingTimeFrame = (
  aiValue: string | null | undefined
): string | undefined => {
  if (!aiValue) return undefined;
  return AI_SHIPPING_TIME_MAP[aiValue] ?? undefined;
};

const hasValue = (value: unknown): boolean => {
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  return value !== null && value !== undefined;
};

export const compactDeep = <T>(value: T): T | undefined => {
  if (Array.isArray(value)) {
    const compactArray = value
      .map((item) => compactDeep(item))
      .filter((item) => item !== undefined);
    return (compactArray.length ? compactArray : undefined) as T | undefined;
  }

  if (value && typeof value === 'object') {
    const compactObject = Object.entries(value as Record<string, unknown>)
      .map(([key, item]) => [key, compactDeep(item)] as const)
      .filter(([, item]) => item !== undefined)
      .reduce(
        (next, [key, item]) => ({
          ...next,
          [key]: item,
        }),
        {} as Record<string, unknown>
      );

    return (Object.keys(compactObject).length ? compactObject : undefined) as
      | T
      | undefined;
  }

  if (value === '' || value === null || value === undefined) {
    return undefined;
  }

  return value;
};

const parseAIBoolean = (
  value: string | null | undefined
): boolean | undefined => {
  if (value === 'True') return true;
  if (value === 'False') return false;
  return undefined;
};

const getBusinessDetails = (
  activationData:
    | StoredActivationData
    | MerchantApplicationResponse
    | null = getStoredActivationData()
): BusinessDetailsResponse | null | undefined => {
  return activationData?.businessDetails;
};

const getBusinessDetailsOrEmpty = (
  activationData:
    | StoredActivationData
    | MerchantApplicationResponse
    | null = getStoredActivationData()
): BusinessDetailsResponse => {
  return getBusinessDetails(activationData) ?? {};
};

const getSavedBusinessAddress = (
  businessDetails?: BusinessDetailsResponse | null
): ActivationLocationDetailsProps['businessAddress'] | undefined => {
  const businessAddress = businessDetails?.businessAddress;

  return Object.values(businessAddress ?? {}).some(hasValue)
    ? businessAddress
    : undefined;
};

export const getStoredAIPrefill = (
  // used by TransactionDetailsStep
  activationData:
    | StoredActivationData
    | MerchantApplicationResponse
    | null = getStoredActivationData()
): BusinessDetailsAISuggestionResponse | null => {
  return activationData?.aiPrefilling?.businessInfo ?? null;
};

const formatActivationAddress = (
  address?: ActivationLocationDetailsProps['businessAddress']
): string => {
  if (!address) {
    return '';
  }

  return [
    address.unitNumber,
    [address.streetNumber, address.streetName].filter(hasValue).join(' '),
    address.suburb,
    address.state,
    address.postcode,
  ]
    .filter(hasValue)
    .join(', ');
};

const hasLocationAIPrefillValues = (
  ai?: BusinessDetailsAISuggestionResponse | null
): boolean => {
  return [ai?.businessLocation, ai?.multipleLocations, ai?.franchiseGroup].some(
    hasValue
  );
};

export const getLocationDetailsAIBannerState = (
  activationData:
    | StoredActivationData
    | MerchantApplicationResponse
    | null = getStoredActivationData()
): ActivationAIBannerState => {
  if (getStoredActivationData()?.businessDetailsStepAAIConfirmed) {
    return 'none';
  }

  const ai = getStoredAIPrefill(activationData);

  const aiFields = [
    meetsConfidence(
      ai?.businessLocationConfidence,
      AI_CONFIDENCE_THRESHOLDS.businessLocation
    ),
    meetsConfidence(
      ai?.multipleLocationsConfidence,
      AI_CONFIDENCE_THRESHOLDS.multipleLocations
    ),
    meetsConfidence(
      ai?.franchiseGroupConfidence,
      AI_CONFIDENCE_THRESHOLDS.franchiseGroup
    ),
  ];

  if (!aiFields.some(Boolean)) {
    return 'none';
  }

  return aiFields.every(Boolean) ? 'full' : 'partial';
};

export const getTransactionDetailsAIBannerState = (
  activationData:
    | StoredActivationData
    | MerchantApplicationResponse
    | null = getStoredActivationData()
): ActivationAIBannerState => {
  if (getStoredActivationData()?.businessDetailsStepBAIConfirmed) {
    return 'none';
  }

  const ai = getStoredAIPrefill(activationData);

  const aiFields = [
    meetsConfidence(
      ai?.avgTransactionValueConfidence,
      AI_CONFIDENCE_THRESHOLDS.avgTransactionValue
    ),
    meetsConfidence(
      ai?.costOfLargestProductConfidence,
      AI_CONFIDENCE_THRESHOLDS.costOfLargestProduct
    ),
    meetsConfidence(
      ai?.avgShipTimeConfidence,
      AI_CONFIDENCE_THRESHOLDS.avgShipTime
    ),
    meetsConfidence(
      ai?.shippingPolicyConfidence,
      AI_CONFIDENCE_THRESHOLDS.shippingPolicy
    ),
    meetsConfidence(
      ai?.returnPolicyConfidence,
      AI_CONFIDENCE_THRESHOLDS.returnPolicy
    ),
  ];

  if (!aiFields.some(Boolean)) {
    return 'none';
  }

  return aiFields.every(Boolean) ? 'full' : 'partial';
};

const buildLocationDetailsFromStorage = (
  activationData:
    | StoredActivationData
    | MerchantApplicationResponse
    | null = getStoredActivationData()
): ActivationLocationDetailsProps => {
  const businessDetails = getBusinessDetails(activationData);
  const savedBusinessAddress = getSavedBusinessAddress(businessDetails);
  // Always read confirmed flags from localStorage — they are localStorage-only
  // fields and are never present on MerchantApplicationResponse (context data).
  const aiConfirmed =
    getStoredActivationData()?.businessDetailsStepAAIConfirmed;
  // Only use saved businessDetails values once the user has explicitly confirmed
  // the step. Before confirmation the API may return default/empty values which
  // should not override AI prefilling.
  if (aiConfirmed) {
    return (compactDeep({
      businessAddress: savedBusinessAddress,
      businessAddressDisplay: savedBusinessAddress
        ? formatActivationAddress(savedBusinessAddress)
        : undefined,
      isPartOfFranchise: businessDetails?.isPartOfFranchise,
      hasMultipleLocations: businessDetails?.hasMultipleLocations,
    }) ?? {}) as ActivationLocationDetailsProps;
  }

  // aiConfirmed is false — ignore saved data and evaluate AI prefill only.
  const ai = getStoredAIPrefill(activationData);

  const locationMeetsThreshold = meetsConfidence(
    ai?.businessLocationConfidence,
    AI_CONFIDENCE_THRESHOLDS.businessLocation
  );
  const businessAddressDisplay = locationMeetsThreshold
    ? ai?.businessLocation
    : undefined;

  if (!hasLocationAIPrefillValues(ai)) {
    return {};
  }

  const result = (compactDeep({
    businessAddressDisplay,
    isPartOfFranchise: meetsConfidence(
      ai?.franchiseGroupConfidence,
      AI_CONFIDENCE_THRESHOLDS.franchiseGroup
    )
      ? parseAIBoolean(ai?.franchiseGroup)
      : undefined,
    hasMultipleLocations: meetsConfidence(
      ai?.multipleLocationsConfidence,
      AI_CONFIDENCE_THRESHOLDS.multipleLocations
    )
      ? parseAIBoolean(ai?.multipleLocations)
      : undefined,
  }) ?? {}) as ActivationLocationDetailsProps;

  return result;
};

const buildTransactionDetailsFromStorage = (
  activationData:
    | StoredActivationData
    | MerchantApplicationResponse
    | null = getStoredActivationData()
): ActivationTransactionDetailsProps => {
  const businessDetails = getBusinessDetails(activationData);
  // Always read confirmed flags from localStorage — they are localStorage-only
  // fields and are never present on MerchantApplicationResponse (context data).
  const aiConfirmed =
    getStoredActivationData()?.businessDetailsStepBAIConfirmed;

  // Only use saved businessDetails values once the user has explicitly confirmed
  // the step. Before confirmation the API may return default/empty values.
  if (aiConfirmed) {
    return (compactDeep({
      averageTransactionValue: businessDetails?.averageTransactionValue,
      costOfLargestProductOrService:
        businessDetails?.costOfLargestProductOrService,
      averageShippingTimeFrame: VALID_SHIPPING_TIME_FRAMES.has(
        businessDetails?.averageShippingTimeFrame ?? ''
      )
        ? businessDetails?.averageShippingTimeFrame
        : undefined,
      hasShippingPolicy: businessDetails?.hasShippingPolicy,
      hasRefundPolicy: businessDetails?.hasRefundPolicy,
    }) ?? {}) as ActivationTransactionDetailsProps;
  }

  const ai = getStoredAIPrefill(activationData);

  return (compactDeep({
    averageTransactionValue: meetsConfidence(
      ai?.avgTransactionValueConfidence,
      AI_CONFIDENCE_THRESHOLDS.avgTransactionValue
    )
      ? ai?.avgTransactionValue
      : undefined,
    costOfLargestProductOrService: meetsConfidence(
      ai?.costOfLargestProductConfidence,
      AI_CONFIDENCE_THRESHOLDS.costOfLargestProduct
    )
      ? ai?.costOfLargestProduct
      : undefined,
    averageShippingTimeFrame: meetsConfidence(
      ai?.avgShipTimeConfidence,
      AI_CONFIDENCE_THRESHOLDS.avgShipTime
    )
      ? mapAIShippingTimeFrame(ai?.avgShipTime)
      : undefined,
    hasShippingPolicy: meetsConfidence(
      ai?.shippingPolicyConfidence,
      AI_CONFIDENCE_THRESHOLDS.shippingPolicy
    )
      ? parseAIBoolean(ai?.shippingPolicy)
      : undefined,
    hasRefundPolicy: meetsConfidence(
      ai?.returnPolicyConfidence,
      AI_CONFIDENCE_THRESHOLDS.returnPolicy
    )
      ? parseAIBoolean(ai?.returnPolicy)
      : undefined,
  }) ?? {}) as ActivationTransactionDetailsProps;
};

const buildProvidedServicesFromStorage = (
  activationData:
    | StoredActivationData
    | MerchantApplicationResponse
    | null = getStoredActivationData()
): ActivationProvidedServicesProps => {
  const businessDetails = getBusinessDetailsOrEmpty(activationData);
  const stepConfirmed =
    getStoredActivationData()?.businessDetailsStepCAIConfirmed;

  return (compactDeep({
    providesService: stepConfirmed
      ? businessDetails.providesService
      : undefined,
    fullPaymentTakeTime: businessDetails.fullPaymentTakeTime,
  }) ?? {}) as ActivationProvidedServicesProps;
};

export const getInitialActivationStepData = <K extends ActivationStepKey>(
  step: K,
  activationData:
    | StoredActivationData
    | MerchantApplicationResponse
    | null = getStoredActivationData()
): Partial<ActivationStepDataModel[K]> => {
  switch (step) {
    case ActivationStepKey.LocationDetails:
      return buildLocationDetailsFromStorage(activationData) as Partial<
        ActivationStepDataModel[K]
      >;
    case ActivationStepKey.TransactionDetails:
      return buildTransactionDetailsFromStorage(activationData) as Partial<
        ActivationStepDataModel[K]
      >;
    case ActivationStepKey.ProvidedServices:
      return buildProvidedServicesFromStorage(activationData) as Partial<
        ActivationStepDataModel[K]
      >;
    default:
      return {};
  }
};

export const hasAIPrefillForStep = (
  step: ActivationStepKey,
  activationData:
    | StoredActivationData
    | MerchantApplicationResponse
    | null = getStoredActivationData()
): boolean => {
  const storedData = activationData as StoredActivationData;
  const ai = getStoredAIPrefill(activationData);

  if (!ai) {
    return false;
  }

  switch (step) {
    case ActivationStepKey.LocationDetails:
      if (storedData?.businessDetailsStepAAIConfirmed) {
        return false;
      }

      return getLocationDetailsAIBannerState(activationData) !== 'none';
    case ActivationStepKey.TransactionDetails:
      if (storedData?.businessDetailsStepBAIConfirmed) {
        return false;
      }

      return (
        meetsConfidence(
          ai.avgTransactionValueConfidence,
          AI_CONFIDENCE_THRESHOLDS.avgTransactionValue
        ) ||
        meetsConfidence(
          ai.costOfLargestProductConfidence,
          AI_CONFIDENCE_THRESHOLDS.costOfLargestProduct
        ) ||
        meetsConfidence(
          ai.shippingPolicyConfidence,
          AI_CONFIDENCE_THRESHOLDS.shippingPolicy
        ) ||
        meetsConfidence(
          ai.avgShipTimeConfidence,
          AI_CONFIDENCE_THRESHOLDS.avgShipTime
        ) ||
        meetsConfidence(
          ai.returnPolicyConfidence,
          AI_CONFIDENCE_THRESHOLDS.returnPolicy
        )
      );
    case ActivationStepKey.ProvidedServices:
      return false;
    default:
      return false;
  }
};

export const getActivationRadioOptions = (
  key: ActivationFormOptionField
): Selectable[] =>
  ACTIVATION_OPTION_DEFINITIONS[key].map(({ value, label }) => ({
    value,
    label,
  }));
